import { useParams } from 'react-router-dom'
import type { DocumentReference } from 'firebase/firestore'
import { useAnonymousAuth } from '@/hooks/useAnonymousAuth'
import { useDocOnce, useDocStream, useCollectionStream } from '@/hooks/useFirestore'
import { alarmRef, parseAlarm } from '@/data/alarms'
import { alertParentRef, alertsQuery, parseAlert } from '@/data/alerts'
import { AppLoading } from '@/components/AppLoading'
import { Spinner } from '@/components/ui/Spinner'
import { ActionsBottomSheet } from '@/components/ActionsBottomSheet'
import { SendReassuranceAnimation } from '@/components/SendReassuranceAnimation'
import { CustomGMap } from '@/map/CustomGMap'
import { ENABLE_MOCK_POSITION } from '@/config/flags'
import { useNotifier } from '@/lib/notifier'
import { appState } from '@/store/appState'
import { valueOrDefault } from '@/lib/format'
import { tr, applyAlarmLocale } from '@/i18n'
import { useEffect } from 'react'
import text from '@/styles/textStyles.module.css'

/* Port of TrackingWidget (pages/tracking/tracking_widget.dart) — the only
 * live screen. M6 scope: the full five-branch state machine; the active
 * branch's Stack (CustomGMap + ActionsBottomSheet + overlay) lands in M7/M8
 * and is a prop-exposing shell for now.
 *
 * Loader levels (spec §5.4):
 *   0  !initialized            -> AppLoading (200x200 pulsing logo)
 *   1  alarms/{id} one-shot    -> AppLoading
 *   2  onSnapshot(alertRef)    -> AppLoading
 *   3  onSnapshot(alerts, orderBy createdAt desc, limit 10) -> 50px spinner
 *
 * Render gate (tracking_widget.dart:143-150) — TWO DIFFERENT alert docs:
 *   list non-empty AND list[0].status==true AND alertDoc.status==true
 *     -> active; list non-empty otherwise -> "marked themselves safe";
 *   list empty -> 'No alerts found'.
 * `containerAlarmsRecord.status` is never read.
 *
 * Parity notes:
 *  - failed anonymous sign-in -> AppLoading forever (no retry);
 *  - missing alertRef: Flutter null-asserts (white screen); here the level-2
 *    hook stays disabled -> AppLoading forever. Same dead end, no crash.
 *  - fallback asymmetry: sheet userName '- ' (trailing space!) vs map '-'. */

export function Tracking() {
  const { id } = useParams()
  const initialized = useAnonymousAuth()
  // context.watch<FFAppState>() — re-render on every PUBLISH (update()),
  // never on silent writes. Drives the overlay + (M8) map props.
  useNotifier(appState, (s) => s.getVersion())

  const alarmR = initialized && id ? alarmRef(id) : null
  const alarm = useDocOnce(alarmR, parseAlarm)

  const alertR = (alarm.data?.data.alertRef ?? null) as DocumentReference | null
  const alert = useDocStream(alertR, parseAlert)

  const parentR = alert.data ? alertParentRef(alert.data.ref) : null
  const alertsQ = parentR ? alertsQuery(parentR) : null
  const alerts = useCollectionStream(alertsQ, parseAlert, parentR?.path ?? null)

  // The alarm's OPTIONAL locale hint ('en' | 'da'), applied once the doc
  // loads and ONLY if the viewer has no explicit language choice. A change
  // re-keys the app subtree (remount); on the second pass this no-ops, so
  // there is no loop. Absent/unknown values are ignored.
  const alarmLocale = alarm.data?.data.locale
  useEffect(() => {
    applyAlarmLocale(alarmLocale)
  }, [alarmLocale])

  return (
    <div data-page="tracking" style={{ height: '100%', background: 'var(--primary-background)' }}>
      {renderState()}
    </div>
  )

  function renderState() {
    // Levels 0-2: the pulsing logo (Flutter wraps level 0 in wrapWithModel,
    // levels 1-2 are bare AppLoadingWidget() — visually identical).
    if (!initialized) return <StateBox state="loading-0"><AppLoading /></StateBox>
    if (alarm.loading) return <StateBox state="loading-1"><AppLoading /></StateBox>
    if (alert.loading) return <StateBox state="loading-2"><AppLoading /></StateBox>

    // Level 3: a DIFFERENT loader — Center(50x50 CircularProgressIndicator).
    if (alerts.loading)
      return (
        <StateBox state="loading-3">
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spinner size={50} />
          </div>
        </StateBox>
      )

    const list = alerts.data ?? []
    // Dart getters: status => _status ?? false; userName => _userName ?? ''.
    const alarmUserName = alarm.data?.data.userName ?? ''
    const alertDocStatus = alert.data?.data.status ?? false
    const listHeadStatus = list[0]?.data.status ?? false

    if (list.length === 0) {
      // Empty: Column(max, center) -> 'No alerts found', Inter 20, #14181B.
      return (
        <StateBox state="empty">
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <p data-empty-text className={text.bodyMedium} style={{ fontSize: 20 }}>
              {tr.tracking.noAlerts}
            </p>
          </div>
        </StateBox>
      )
    }

    if (listHeadStatus && alertDocStatus) {
      // ACTIVE — the Stack in paint order (spec §5.4): CustomGMap (M8) fills
      // the screen; Align(0,1) -> ActionsBottomSheet; conditional overlay
      // Align(0,0) -> SendReassuranceAnimation. Dead `if (false)` branches
      // (trip_origin image, FlutterFlowGoogleMap, DraggableScrollableSheet)
      // are not ported.
      const head = list[0]!
      return (
        <div
          data-state="active"
          data-map-username={valueOrDefault(alarmUserName, tr.tracking.fallbackNameMap)}
          data-user-id={parentR!.id}
          data-destination={JSON.stringify(head.data.lastLocation ?? null)}
          data-first-point={JSON.stringify(head.data.firstPointLocation?.location ?? null)}
          style={{ position: 'relative', height: '100%', overflow: 'hidden' }}
        >
          {/* PointerInterceptor -> CustomGMap, full-screen. Props exactly as
              tracking_widget.dart passes them (spec §5.4), including the
              kDebugMode testPossition mock and the Dart force-unwraps. */}
          <CustomGMap
            currentLocation={
              ENABLE_MOCK_POSITION ? appState.testPossition : appState.myPosition
            }
            destination={head.data.lastLocation!}
            // LITERAL hex, as in the original (Color(0xFF1F4CF1) is a widget
            // literal there too). NOT a CSS var: this string goes into the
            // google.maps.Polyline/Circle renderer, which cannot resolve
            // var() and silently falls back to black.
            polylineColor="#1F4CF1"
            directionFor={appState.selectedDirectionFor!}
            bottomSheetView={appState.currentBottomSheetView!}
            userName={valueOrDefault(alarmUserName, tr.tracking.fallbackNameMap)}
            firstPoinLocation={head.data.firstPointLocation!.location!}
          />

          {/* Align(0,1) -> PointerInterceptor -> ActionsBottomSheet */}
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0 }}>
            <ActionsBottomSheet
              userName={valueOrDefault(alarmUserName, tr.tracking.fallbackNameSheet)}
              alerts={list}
              userId={parentR!.id}
            />
          </div>

          {/* if (FFAppState().sendReassuranceAnimation) -> Align(0,0) */}
          {appState.sendReassuranceAnimation && (
            <div
              data-reassurance-overlay
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <SendReassuranceAnimation />
            </div>
          )}
        </div>
      )
    }

    // SAFE: wordmark + '<name> has marked themselves safe...💞'.
    return (
      <StateBox state="safe">
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <p className={text.wordmark}>{tr.brand}</p>
          <p
            data-safe-text
            style={{
              padding: '0 24px',
              fontFamily: 'var(--font-poppins-medium)',
              fontWeight: 500,
              fontSize: 20,
              textAlign: 'center',
              color: 'var(--primary-text)',
            }}
          >
            {tr.tracking.markedSafe(valueOrDefault(alarmUserName, tr.tracking.fallbackName))}
          </p>
        </div>
      </StateBox>
    )
  }
}

function StateBox({ state, children }: { state: string; children: React.ReactNode }) {
  return (
    <div data-state={state} style={{ height: '100%' }}>
      {children}
    </div>
  )
}
